import { createSlice } from "@reduxjs/toolkit";
import { CampaignsSlice } from "types/campaign";
import { fetchCampaign, fetchCampaignActivityThunk, fetchCampaignsCount, fetchCampaignsList, markCampaignCompleted, sendCampaignForApprovalRequest } from "./thunk";
import { ERROR } from "types";

const initialState: CampaignsSlice = {
    data: {},
    loading: true,
    error: null,
    items: {},
    totals: {},
    activity: {
        list: [],
        loading: true,
        last_fetched_at: null
    }
}

const campaigns = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {
        updateCampaignsCount: (state, { payload }) => {
            const { key, value, brand_id } = payload;

            state.totals = {
                ...state.totals,
                [brand_id]: {
                    ...(state.totals[brand_id] ?? {}),
                    data: {
                        ...(state.totals[brand_id]?.data ?? {}),
                        [key]: value
                    }
                }
            }

            state.totals[brand_id].data[key] = value;
        },
        updateRemainingBudget: (state, {payload}) => {
            const {campaignId, budget} = payload;
            state.items[campaignId] = {
                ...state.items[campaignId],
                data: {
                    ...state.items[campaignId].data,
                    campaign: {
                        ...state.items[campaignId].data.campaign,
                        remaining_budget: budget
                    }
                }
            }
        },
        markCampaignDataExpired: (state, {payload}) => {
            const {campaignId} = payload;
            state.items[campaignId] = {
                ...state.items[campaignId],
                expired: true
            }
        },
        refetchCampaignLists: (state) => {
            state.data = {};
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaignsList.pending, (state) => {
                state.loading = true
                state.error = null;
            })
            .addCase(fetchCampaignsList.fulfilled, (state, { payload }) => {

                const { status, brand_id, campaigns, page } = payload;

                const existingData = state.data[brand_id] ?? {};

                const existingList = existingData[status]?.list ?? [];
                const list = page === 1 ? campaigns.items : [...existingList, ...campaigns.items];

                state.data[brand_id] = {
                    ...existingData,
                    [status] : {
                        list,
                        last_fetched_at: Date.now(),
                        page,
                        has_more: campaigns.count > list.length,
                        count: campaigns.count,
                        expired: false
                    }
                }

                state.loading = false;
                state.error = null;
            })
            .addCase(fetchCampaignsList.rejected, (state, action) => {
                state.loading = false;
                if(action.payload !== ERROR.CACHED)
                    state.error = action.payload || 'Failed to fetch campaigns'
            })

            .addCase(fetchCampaign.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCampaign.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                state.items[action.payload.campaignId] = {
                    data: action.payload.campaignData,
                    last_fetched_at: Date.now(),
                    expired: false,
                }
            })
            .addCase(fetchCampaign.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch campaign!"
            })

            .addCase(markCampaignCompleted.fulfilled, (state, { payload }) => {
                const { campaignId } = payload;

                state.items[campaignId] = {
                    ...state.items[campaignId],
                    data: {
                        ...state.items[campaignId].data,
                        campaign: {
                            ...state.items[campaignId].data.campaign,
                            status: 'completed'
                        }
                    }
                }

            })

            .addCase(sendCampaignForApprovalRequest.fulfilled, (state, { payload }) => {
                const { campaignId } = payload;
                state.items[campaignId] = {
                    ...(state.items[campaignId] ?? {}),
                    data: {
                        ...(state.items[campaignId]?.data ?? {}),
                        campaign: {
                            ...(state.items[campaignId]?.data?.campaign ?? {}),
                            status: 'pending'
                        }
                    }
                }
            })

            .addCase(fetchCampaignsCount.fulfilled, (state, { payload }) => {
                const { totals, brand_id } = payload; 
                state.totals[brand_id] = {
                    data: totals,
                    last_fetched_at: Date.now()
                }
            })
            .addCase(fetchCampaignActivityThunk.pending, (state) => {
                state.activity.loading = true;
            })
            .addCase(fetchCampaignActivityThunk.fulfilled, (state, {payload}) => {
                state.activity.list = payload.items;
                state.activity.last_fetched_at = Date.now();
                state.activity.loading = false;
            })
            .addCase(fetchCampaignActivityThunk.rejected, (state) => {
                state.activity.loading = false;
            })

    }
})

export const { updateCampaignsCount, updateRemainingBudget, markCampaignDataExpired, refetchCampaignLists } = campaigns.actions;

export default campaigns.reducer;